import { useCallback, useEffect, useRef } from 'react';
import { useToast } from '@practium/ui';
import {
	useGetOcrVerify,
	usePostOcrExtractFoliar,
	usePostOcrExtractSoil,
	type OcrFoliarSample,
	type OcrSoilSample,
} from '@practium/features/api';

import { useUserOcrStorage } from './useUserOcrStorage';
import { OCR_TIMEOUT_MS, OCR_TIMEOUT_MESSAGE } from '../../api/hooks/ocr/ocr.constants';
import { findOcrWarningForSample } from '../utils/ocrWarnings';

type AnalysisType = 'soil' | 'foliar';

interface PersistedImport {
	jobId: string;
	sampleIndex: number;
	total: number;
}

const storageKey = (type: AnalysisType) => `practium:ocr-import:${type}`;

export interface UseOcrImportResult<TSample> {
	uploadFile: (file: File) => Promise<void>;
	isUploading: boolean;
	isProcessing: boolean;
	isBusy: boolean;
	jobId: string;
	status?: 'queued' | 'processing' | 'done' | 'failed';
	samples: TSample[];
	totalSamples: number;
	currentIndex: number;
	currentSample?: TSample;
	warning: boolean;
	warningMessage: string;
	advance: () => boolean;
	reset: () => void;
	hasPendingImport: boolean;
	loadingMessage: string;
}

export const useOcrImport = <TSample extends OcrSoilSample | OcrFoliarSample>(
	analysisType: AnalysisType,
	enabled: boolean
): UseOcrImportResult<TSample> => {
	const toast = useToast();
	const showTimeoutError = toast.error;

	const [persisted, persist, isCurrent] = useUserOcrStorage<PersistedImport | null>(storageKey(analysisType), null);
	const jobId = enabled ? persisted?.jobId ?? '' : '';
	const currentIndex = enabled ? persisted?.sampleIndex ?? 0 : 0;

	const announcedJobId = useRef('');
	const handledFailedJobId = useRef('');
	useEffect(() => {
		announcedJobId.current = '';
		handledFailedJobId.current = '';
	}, [isCurrent]);

	const foliarExtract = usePostOcrExtractFoliar();
	const soilExtract = usePostOcrExtractSoil();
	const extractMutation = analysisType === 'foliar' ? foliarExtract : soilExtract;

	const [{ data: verify, isFetching }] = useGetOcrVerify<TSample>([jobId], enabled);

	const status = verify?.status;
	const isUploading = extractMutation.isPending;
	const isProcessing = !!jobId && status !== 'done' && status !== 'failed';
	const isBusy = isUploading || isProcessing || (isFetching && !!jobId && status !== 'done');

	const samples = (status === 'done' ? (verify?.result?.samples ?? []) : []) as TSample[];
	const totalSamples = samples.length;
	const currentSample = samples[currentIndex];
	const currentWarning = findOcrWarningForSample(verify?.warnings, currentSample);
	const warning = !!currentWarning;
	const warningMessage = currentWarning?.message ?? '';

	const reset = useCallback(() => {
		announcedJobId.current = '';
		handledFailedJobId.current = '';
		persist(null);
	}, [persist]);

	useEffect(() => {
		if (!enabled || !jobId || status === 'done' || status === 'failed') return;
		const timer = window.setTimeout(() => {
			reset();
			showTimeoutError(OCR_TIMEOUT_MESSAGE);
		}, OCR_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [enabled, jobId, status, reset, showTimeoutError]);

	const uploadFile = useCallback(
		async (file: File) => {
			if (!enabled || !isCurrent()) return;
			try {
				announcedJobId.current = '';
				handledFailedJobId.current = '';
				persist(null);
				const run = extractMutation.mutateAsync as (vars: {
					file: File;
				}) => Promise<{ jobId: string }>;
				const response = await run({ file });
				if (!isCurrent()) return;
				persist({ jobId: response.jobId, sampleIndex: 0, total: 0 });
			} catch (error) {
				if (!isCurrent()) return;
				console.error('Erro ao enviar arquivo para OCR:', error);
				toast.error('Não foi possível enviar o laudo. Verifique o arquivo e tente novamente.');
			}
		},
		[extractMutation, persist, toast, enabled, isCurrent]
	);

	const advance = useCallback(() => {
		const nextIndex = currentIndex + 1;
		if (nextIndex >= totalSamples) return false;
		persist({ jobId, sampleIndex: nextIndex, total: totalSamples });
		return true;
	}, [currentIndex, jobId, persist, totalSamples]);

	useEffect(() => {
		if (status !== 'failed' || !verify) return;
		if (handledFailedJobId.current === verify.jobId) return;
		handledFailedJobId.current = verify.jobId;
		persist(null);
		const message = verify.error?.message?.trim();
		toast.error(
			message
				? `Falha ao ler o laudo: ${message}`
				: 'Não foi possível ler o laudo automaticamente. Preencha os campos manualmente ou tente outro arquivo.'
		);
	}, [status, verify, persist, toast]);

	useEffect(() => {
		if (status !== 'done' || !verify) return;
		if (announcedJobId.current === verify.jobId) return;

		const list = verify.result?.samples ?? [];

		if (list.length === 0) {
			announcedJobId.current = verify.jobId;
			persist(null);
			toast.error(
				analysisType === 'soil'
					? 'Nenhuma análise foi identificada. Confira se o laudo enviado é realmente de solo.'
					: 'Nenhuma análise foi identificada. Confira se o laudo enviado é realmente foliar.'
			);
			return;
		}

		if (verify.analysisType && verify.analysisType !== analysisType) {
			announcedJobId.current = verify.jobId;
			persist(null);
			toast.error(
				analysisType === 'soil'
					? 'O laudo enviado parece ser de análise foliar. Importe-o na tela de análise foliar.'
					: 'O laudo enviado parece ser de análise de solo. Importe-o na tela de análise de solo.'
			);
			return;
		}

		announcedJobId.current = verify.jobId;
		persist({ jobId: verify.jobId, sampleIndex: currentIndex, total: list.length });
		toast.info(
			list.length > 1
				? `Dados carregados (${list.length} análises). Revise e salve cada uma.`
				: 'Dados carregados. Revise os campos e salve.'
		);
	}, [status, verify, analysisType, currentIndex, persist, toast]);

	const hasPendingImport = enabled && status === 'done' && totalSamples > 0 && !!jobId;

	const loadingMessage = isUploading
		? 'Enviando o laudo para a Flora...'
		: 'A Flora está lendo o laudo, aguarde um instante...';

	return {
		uploadFile,
		isUploading,
		isProcessing,
		isBusy,
		jobId,
		status,
		samples,
		totalSamples,
		currentIndex,
		currentSample,
		warning,
		warningMessage,
		advance,
		reset,
		hasPendingImport,
		loadingMessage,
	};
};
